/**
 * "Which language does this variation TITLE name" (composer PREFILL only; the stored `language` stays the SSOT).
 *
 * MIRROR of backend/src/utils/variation-language.ts (`classifyTitleLanguage` and its tables). The backend file is
 * the SSOT: change the tables/rules THERE first, then copy them here unchanged.
 */

const SUPPORTED_CODES = ['en', 'es', 'de', 'fr', 'it', 'pt', 'nl', 'ja', 'zh', 'ko', 'ar', 'ru', 'pl'] as const;

/** Language NAMES per supported code, ALREADY FOLDED (lowercase, no accents). */
const LANGUAGE_NAME_TABLE: Readonly<Record<(typeof SUPPORTED_CODES)[number], readonly string[]>> = {
  en: ['english', 'englisch', 'englische', 'ingles', 'anglais', 'angielski'],
  de: ['german', 'deutsch', 'deutsche', 'aleman', 'allemand', 'niemiecki'],
  es: ['spanish', 'spanisch', 'spanische', 'espanol', 'espagnol', 'hiszpanski'],
  fr: ['french', 'franzosisch', 'franzosische', 'frances', 'francais', 'francuski'],
  it: ['italian', 'italienisch', 'italienische', 'italiano', 'italien', 'wloski'],
  pt: ['portuguese', 'portugiesisch', 'portugiesische', 'portugues', 'portugais', 'portugalski'],
  nl: ['dutch', 'niederlandisch', 'niederlandische', 'neerlandes', 'holandes', 'nederlands', 'neerlandais', 'hollandais', 'niderlandzki', 'holenderski'],
  ja: ['japanese', 'japanisch', 'japanische', 'japones', 'japonais', 'japonski'],
  zh: ['chinese', 'chinesisch', 'chinesische', 'chino', 'chinois', 'chinski'],
  ko: ['korean', 'koreanisch', 'koreanische', 'coreano', 'coreen', 'koreanski'],
  ar: ['arabic', 'arabisch', 'arabische', 'arabe', 'arabski'],
  ru: ['russian', 'russisch', 'russische', 'ruso', 'russe', 'rosyjski'],
  pl: ['polish', 'polnisch', 'polnische', 'polaco', 'polonais', 'polski'],
};

/** Words that follow a language name in a "<name> version" title (folded). */
const TITLE_VERSION_WORDS: readonly string[] = ['version', 'versions', 'fassung', 'wersja', 'variante', 'variant'];

/** Codes that are also ordinary words ("it", "pl", "ar"): never auto-labelled from the code alone. */
const AMBIGUOUS_TITLE_CODES: readonly string[] = ['it', 'ar', 'pl'];

/** Codes that are also common words in another language: a lowercase code + bare space is NOT a label. */
const WORD_LIKE_TITLE_CODES: readonly string[] = ['de', 'en', 'es', 'ja', 'ko', 'nl', 'pt', 'ru', 'zh', 'fr'];

/** Explicit separators after a leading code/name (a plain space is the 7th). */
const TITLE_SEPARATOR_CHARS = '—–\\-:·|';

/** Native-script names as written; FOLDED when the lookup map is built (NFD decomposes й and Hangul). */
export const NATIVE_SCRIPT_NAMES: Readonly<Record<string, string>> = {
  '日本語': 'ja',
  '中文': 'zh',
  '한국어': 'ko',
  'العربية': 'ar',
  'русский': 'ru',
};

/** Lowercase, strip accents, "l-stroke" to l, "sharp s" to ss, trim, collapse whitespace. */
const foldForLanguageMatch = (raw: string): string =>
  raw
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/ł/g, 'l')
    .replace(/ß/g, 'ss')
    .replace(/\s+/g, ' ')
    .trim();

const NAME_TO_CODE: ReadonlyMap<string, string> = new Map<string, string>([
  ...Object.entries(LANGUAGE_NAME_TABLE).flatMap(([code, names]) => names.map((n): [string, string] => [n, code])),
  ...Object.entries(NATIVE_SCRIPT_NAMES).map(([n, code]): [string, string] => [foldForLanguageMatch(n), code]),
]);

const SUPPORTED_SET: ReadonlySet<string> = new Set(SUPPORTED_CODES);
const NAMES_RE = [...NAME_TO_CODE.keys()].map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
const VERSION_RE = TITLE_VERSION_WORDS.join('|');
const SEP_RE = `(?:\\s*[${TITLE_SEPARATOR_CHARS}]\\s*|\\s+)`;
const EXACT_NAME_VERSION = new RegExp(`^(${NAMES_RE})\\s+(?:${VERSION_RE})$`);
const LEADING_NAME = new RegExp(`^(${NAMES_RE})${SEP_RE}\\S`);
const LEADING_CODE = new RegExp(`^([a-z]{2})(${SEP_RE})\\S`);

/** Trivial normalisation: trim, lowercase, "_" to "-". Empty gives null. */
const normalizeLanguageCode = (raw: string): string | null => {
  const code = raw.trim().toLowerCase().replace(/_/g, '-');
  return code.length > 0 ? code : null;
};

/**
 * Order: 1. exact code ("de", "pt-br")  2. exact name ("Deutsch")  3. "<name> version"  4. leading name + separator
 * ("Deutsch: Teaser")  5. leading code + separator ("FR - quote 02"); ambiguous codes are NOT resolved.
 * Returns the code (lowercase) or null.
 */
export const languageFromTitle = (title: string | null | undefined): string | null => {
  const trimmed = (title ?? '').trim();
  if (trimmed.length === 0) return null;
  const folded = foldForLanguageMatch(trimmed);

  // 1. exact code (optionally with a region)
  const exact = normalizeLanguageCode(trimmed);
  if (exact !== null && /^[a-z]{2}(-[a-z0-9]{2,4})?$/.test(exact) && SUPPORTED_SET.has(exact.slice(0, 2))) {
    return AMBIGUOUS_TITLE_CODES.includes(exact.slice(0, 2)) ? null : exact;
  }
  // 2. exact name
  const byName = NAME_TO_CODE.get(folded);
  if (byName) return byName;
  // 3. "<name> version"
  const nv = EXACT_NAME_VERSION.exec(folded);
  if (nv) return NAME_TO_CODE.get(nv[1]) ?? null;
  // 4. leading name + separator
  const ln = LEADING_NAME.exec(folded);
  if (ln) return NAME_TO_CODE.get(ln[1]) ?? null;
  // 5. leading code + separator
  const lc = LEADING_CODE.exec(folded);
  if (lc && SUPPORTED_SET.has(lc[1])) {
    const code = lc[1];
    if (AMBIGUOUS_TITLE_CODES.includes(code)) return null;
    const explicitSeparator = lc[2].trim().length > 0;
    const upper = trimmed.slice(0, 2) === trimmed.slice(0, 2).toUpperCase();
    if (explicitSeparator || upper || !WORD_LIKE_TITLE_CODES.includes(code)) return code;
    return null;
  }
  return null;
};
